// re-push trigger
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import BackButton from '../components/BackButton';
import {
  Box,
  Typography,
  ButtonGroup,
  Button,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
  IconButton,
  Pagination,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const StudentManagerPage = () => {
  const token = localStorage.getItem('teacherToken');
  const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };

  // data
  const [allStudents, setAllStudents] = useState([]);
  const [grades, setGrades] = useState([]);
  const [lessons, setLessons] = useState([]); // lessons for edit form
  const [loading, setLoading] = useState(false);

  // edit
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    code: '',
    name: '',
    parent: { name: '', phone: '', email: '' },
    gradeId: null,
    lessonIds: []
  });

  // filters
  const [filterMode, setFilterMode] = useState('all'); // 'all' | 'grade' | 'lesson'
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedLesson, setSelectedLesson] = useState('');

  // search & pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  // multi-select
  const [selectedIds, setSelectedIds] = useState([]);

  // fetch grades
  const fetchGrades = async () => {
    try {
      const res = await axios.get('https://education-backend.liara.run/api/grade', axiosConfig);
      setGrades(res.data || []);
    } catch (err) {
      console.error('❌ خطا در دریافت پایه‌ها:', err);
    }
  };

  // fetch lessons for selected grade (used for edit form)
  const fetchLessons = async (gradeId) => {
    if (!gradeId) return setLessons([]);
    try {
      const res = await axios.get(`https://education-backend.liara.run/api/lesson?gradeId=${gradeId}`, axiosConfig);
      setLessons(res.data || []);
    } catch (err) {
      console.error('❌ خطا در دریافت درس‌ها:', err);
      setLessons([]);
    }
  };

  // fetch students
  const fetchStudents = async () => {
    setLoading(true);
    try {
      const res = await axios.get('https://education-backend.liara.run/api/student', axiosConfig);
      setAllStudents(res.data || []);
    } catch (err) {
      console.error('❌ خطا در دریافت دانش‌آموزان:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGrades();
    fetchStudents();
  }, []);

  // when filterMode=lesson and grade changes, load lessons for filter select
  useEffect(() => {
    if (filterMode === 'lesson' && selectedGrade) {
      fetchLessons(selectedGrade);
    } else {
      setLessons([]);
      setSelectedLesson('');
    }
  }, [filterMode, selectedGrade]);

  // combined filter + search
  const filteredStudents = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const gradeObj = grades.find((g) => g.id === Number(selectedGrade));
    const gradeTitle = gradeObj ? `${gradeObj.level} - ${gradeObj.stream}` : '';

    return allStudents.filter((s) => {
      const matchesSearch =
        (s.name ?? '').toLowerCase().includes(term) || (s.code ?? '').toLowerCase().includes(term);

      let matchesFilter = true;
      if (filterMode === 'grade' && selectedGrade) {
        matchesFilter = (s.lessons || []).some((l) => (l.gradeTitle ?? l.GradeTitle) === gradeTitle);
      } else if (filterMode === 'lesson' && selectedLesson) {
        matchesFilter = (s.lessons || []).some((l) => (l.id ?? l.Id) === Number(selectedLesson));
      }

      return matchesSearch && matchesFilter;
    });
  }, [allStudents, searchTerm, filterMode, selectedGrade, selectedLesson, grades]);

  // pagination
  const pageCount = Math.max(1, Math.ceil(filteredStudents.length / rowsPerPage));
  const paginatedStudents = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredStudents.slice(start, start + rowsPerPage);
  }, [filteredStudents, currentPage]);

  // edit handlers
  const handleEditInit = async (s) => {
    setEditingId(s.id);

    // determine gradeId for this student if available (try from lessons)
    const studentGradeId =
      s.lessons && s.lessons.length > 0
        ? (s.lessons[0].gradeId ?? s.lessons[0].GradeId ?? null)
        : null;

    // load lessons for that grade so user can choose lessons
    if (studentGradeId) {
      await fetchLessons(studentGradeId);
    } else {
      setLessons([]);
    }

    setEditForm({
      code: s.code || '',
      name: s.name || '',
      parent: {
        name: s.parent?.name ?? '',
        phone: s.parent?.phone ?? '',
        email: s.parent?.email ?? ''
      },
      gradeId: studentGradeId,
      lessonIds: Array.isArray(s.lessons) ? s.lessons.map((l) => l.id ?? l.Id) : []
    });
  };

  const submitEdit = async () => {
    try {
      const payload = {
        code: editForm.code,
        name: editForm.name,
        parent: {
          name: editForm.parent.name || '',
          phone: editForm.parent.phone || '',
          email: editForm.parent.email || ''
        },
        lessonIds: editForm.lessonIds || [],
        gradeId: editForm.gradeId || 0
      };

      await axios.put(
        `https://education-backend.liara.run/api/student/${editingId}`,
        payload,
        axiosConfig
      );
      setEditingId(null);
      fetchStudents();
    } catch (err) {
      console.error('❌ خطا در ویرایش دانش‌آموز:', err);
    }
  };

  // delete handlers
  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این دانش‌آموز مطمئن هستید؟')) return;
    try {
      await axios.delete(
        `https://education-backend.liara.run/api/student/${id}`,
        axiosConfig
      );
      fetchStudents();
    } catch (err) {
      console.error('❌ خطا در حذف دانش‌آموز:', err);
    }
  };

  const handleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(paginatedStudents.map((s) => s.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleDeleteSelected = async () => {
    if (!window.confirm('آیا از حذف این دانش‌آموزان مطمئن هستید؟')) return;
    try {
      await Promise.all(
        selectedIds.map((id) =>
          axios.delete(`https://education-backend.liara.run/api/student/${id}`, axiosConfig)
        )
      );
      setSelectedIds([]);
      fetchStudents();
    } catch (err) {
      console.error('❌ خطا در حذف گروهی:', err);
    }
  };

  // when user changes grade in edit form, reload lessons for that grade
  const handleEditGradeChange = async (gradeId) => {
    setEditForm((f) => ({ ...f, gradeId, lessonIds: [] }));
    if (gradeId) await fetchLessons(gradeId);
    else setLessons([]);
  };

  // helper for multi-select lesson change
  const toggleLessonInEdit = (lessonId) => {
    setEditForm((f) => {
      const setIds = new Set(f.lessonIds || []);
      if (setIds.has(lessonId)) setIds.delete(lessonId);
      else setIds.add(lessonId);
      return { ...f, lessonIds: Array.from(setIds) };
    });
  };

  // helper: derive grade title for display from student's lessons (first lesson)
  const getStudentGradeTitle = (s) => {
    const l = (s.lessons || [])[0];
    if (!l) return '---';
    return l.gradeTitle ?? (l.level && l.stream ? `${l.level} - ${l.stream}` : '---');
  };

  return (
    <Box sx={{ p: 2 }} dir="rtl">
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <BackButton toFallback="/dashboard" />
        <Typography variant="h4">👩‍🎓 مدیریت دانش‌آموزان</Typography>
      </header>

      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <ButtonGroup variant="outlined">
          <Button onClick={() => { setFilterMode('all'); setSelectedGrade(''); setSelectedLesson(''); }}>
            همه
          </Button>
          <Button onClick={() => { setFilterMode('grade'); setSelectedLesson(''); }}>
            بر اساس پایه
          </Button>
          <Button onClick={() => setFilterMode('lesson')}>
            بر اساس درس
          </Button>
        </ButtonGroup>

        {filterMode !== 'all' && (
          <TextField
            select
            label="انتخاب پایه"
            value={selectedGrade}
            onChange={(e) => { setSelectedGrade(e.target.value); setCurrentPage(1); }}
            SelectProps={{ native: true }}
            size="small"
          >
            <option value="" />
            {grades.map((g) => (
              <option key={g.id} value={g.id}>
                {g.level} - {g.stream}
              </option>
            ))}
          </TextField>
        )}

        {filterMode === 'lesson' && (
          <TextField
            select
            label="انتخاب درس"
            value={selectedLesson}
            onChange={(e) => { setSelectedLesson(e.target.value); setCurrentPage(1); }}
            SelectProps={{ native: true }}
            size="small"
          >
            <option value="" />
            {lessons.map((l) => (
              <option key={l.id} value={l.id}>
                {l.title}
              </option>
            ))}
          </TextField>
        )}

        <TextField
          label="جستجو بر اساس نام یا کد"
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          fullWidth
          size="small"
          sx={{ flex: 1 }}
        />
      </Box>

      {loading ? (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {paginatedStudents.length === 0 ? (
            <Typography color="text.secondary">هیچ دانش‌آموزی یافت نشد.</Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.length === paginatedStudents.length && paginatedStudents.length > 0}
                        indeterminate={selectedIds.length > 0 && selectedIds.length < paginatedStudents.length}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </TableCell>
                    <TableCell align="right">کد</TableCell>
                    <TableCell align="right">نام</TableCell>
                    <TableCell align="right">پایه</TableCell>
                    <TableCell align="right">درس‌ها</TableCell>
                    <TableCell align="center">اقدامات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedStudents.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.includes(s.id)}
                          onChange={() => handleSelect(s.id)}
                        />
                      </TableCell>

                      {editingId === s.id ? (
                        <>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              value={editForm.code}
                              onChange={(e) =>
                                setEditForm({ ...editForm, code: e.target.value })
                              }
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              value={editForm.name}
                              onChange={(e) =>
                                setEditForm({ ...editForm, name: e.target.value })
                              }
                            />
                          </TableCell>

                          <TableCell align="right">
                            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                              <InputLabel id="edit-grade-label">پایه</InputLabel>
                              <Select
                                labelId="edit-grade-label"
                                value={editForm.gradeId ?? ''}
                                label="پایه"
                                onChange={(e) => handleEditGradeChange(e.target.value)}
                              >
                                <MenuItem value="">
                                  <em>انتخاب نشده</em>
                                </MenuItem>
                                {grades.map((g) => (
                                  <MenuItem key={g.id} value={g.id}>
                                    {g.level} - {g.stream}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>

                            <div>
                              <Typography variant="caption">انتخاب درس‌ها</Typography>
                              <Box sx={{ maxHeight: 160, overflow: 'auto', border: '1px solid #eee', borderRadius: 1, p: 1 }}>
                                {lessons.length === 0 ? (
                                  <Typography variant="body2" color="text.secondary">برای پایه انتخاب‌شده درسی یافت نشد.</Typography>
                                ) : (
                                  lessons.map((l) => {
                                    const lid = l.id ?? l.Id;
                                    const checked = (editForm.lessonIds || []).includes(lid);
                                    return (
                                      <Box key={lid} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Checkbox checked={checked} onChange={() => toggleLessonInEdit(lid)} />
                                        <Typography variant="body2">{l.title ?? l.Title}</Typography>
                                      </Box>
                                    );
                                  })
                                )}
                              </Box>
                            </div>
                          </TableCell>

                          <TableCell align="center">
                            <Button size="small" variant="contained" onClick={submitEdit}>
                              ذخیره
                            </Button>
                            <Button size="small" onClick={() => setEditingId(null)}>
                              لغو
                            </Button>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell align="right">{s.code}</TableCell>
                          <TableCell align="right">{s.name}</TableCell>
                          <TableCell align="right">{getStudentGradeTitle(s)}</TableCell>
                          <TableCell align="right">
                            {(s.lessons || []).map((l) => l.title ?? l.Title).join(', ')}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton size="small" onClick={() => handleEditInit(s)}>
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(s.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {selectedIds.length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeleteSelected}
              >
                حذف انتخاب‌شده‌ها ({selectedIds.length})
              </Button>
            </Box>
          )}

          {filteredStudents.length > rowsPerPage && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Pagination
                count={pageCount}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default StudentManagerPage;